/**
 * Trade Account Form Enhancement
 * Handles dynamic field visibility based on company type
 * Formats form data before submission for better email readability
 */

class TradeAccountForm {
  /** @type {HTMLFormElement} */
  form;

  /** @type {HTMLSelectElement | null} */
  companyTypeSelect = null;

  /**
   * @param {HTMLFormElement} formElement - The form element to enhance
   */
  constructor(formElement) {
    this.form = formElement;
    this.init();
  }

  init() {
    if (!this.form) return;

    this.companyTypeSelect = /** @type {HTMLSelectElement | null} */ (
      this.form.querySelector('#TradeAccountForm-company-type')
    );

    if (this.companyTypeSelect) {
      this.companyTypeSelect.addEventListener('change', (e) => {
        const target = /** @type {HTMLSelectElement} */ (e.target);
        this.handleCompanyTypeChange(target.value);
      });
      this.handleCompanyTypeChange(this.companyTypeSelect.value);
    }

    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  /**
   * Handle form submission
   * @param {SubmitEvent} e - Submit event
   */
  handleSubmit(e) {
    if (!this.form.checkValidity()) return;

    e.preventDefault();

    const submitBtn = /** @type {HTMLButtonElement | null} */ (
      this.form.querySelector('button[type="submit"]')
    );
    const loader = this.form.querySelector('.trade-account-form__loader');
    const emailField = /** @type {HTMLInputElement | null} */ (
      this.form.querySelector('input[name="contact[email]"]')
    );
    const emailValue = emailField?.value || '';

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting...';
    }
    this.form.classList.add('is-submitting');
    loader?.classList.add('is-visible');

    this.formatFormData();
    this.removeFieldNames();

    if (emailField && emailValue) {
      emailField.value = emailValue;
    }

    setTimeout(() => this.form.submit(), 10);
  }

  /**
   * Toggle company-specific fields visibility
   * @param {string} companyType - Selected company type
   */
  handleCompanyTypeChange(companyType) {
    const allFields = /** @type {NodeListOf<HTMLElement>} */ (
      this.form.querySelectorAll('.trade-account-form__company-fields')
    );
    allFields.forEach((field) => {
      field.style.display = 'none';
    });

    if (!companyType) return;

    const typeFields = /** @type {NodeListOf<HTMLElement>} */ (
      this.form.querySelectorAll(`.trade-account-form__company-fields[data-company-type="${companyType}"]`)
    );
    typeFields.forEach((field) => {
      field.style.display = 'block';
    });

    const commonFields = /** @type {NodeListOf<HTMLElement>} */ (
      this.form.querySelectorAll('.trade-account-form__company-fields[data-company-type="common"]')
    );
    commonFields.forEach((field) => {
      field.style.display = 'block';
    });
  }

  /**
   * Format date to readable string
   * @param {Date} date - Date object
   * @returns {string} Formatted date string
   */
  formatDate(date) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${month} ${day}, ${year} at ${hours}:${minutes}`;
  }

  /**
   * Create formatted row for email body
   * @param {string} label - Field label
   * @param {string} value - Field value
   * @returns {string} Formatted row
   */
  createEmailRow(label, value) {
    return `  ${label}: ${value || 'Not provided'}\n`;
  }

  /**
   * Remove name attributes from fields to prevent duplicate data in email
   */
  removeFieldNames() {
    const keepFields = ['contact[body]', 'contact[email]'];
    const inputs = /** @type {NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>} */ (
      this.form.querySelectorAll('input, select, textarea')
    );

    inputs.forEach((input) => {
      if (input.name?.startsWith('contact[') && !keepFields.includes(input.name)) {
        input.removeAttribute('name');
        if (input instanceof HTMLSelectElement) {
          input.selectedIndex = 0;
        } else {
          input.value = '';
        }
      }
    });
  }

  /**
   * Format form data into professional email body
   */
  formatFormData() {
    const formData = new FormData(this.form);
    const bodyTextarea = /** @type {HTMLTextAreaElement | null} */ (
      this.form.querySelector('textarea[name="contact[body]"]')
    );
    const additionalInfoTextarea = /** @type {HTMLTextAreaElement | null} */ (
      this.form.querySelector('textarea[name="contact[additional_info]"]')
    );

    if (!bodyTextarea) return;

    const formContainer = /** @type {HTMLElement | null} */ (this.form.closest('.trade-account-form'));
    const shopName = formContainer?.dataset.shopName || 'Store';
    const formattedDate = this.formatDate(new Date());

    /**
     * Get form value by name
     * @param {string} name - Field name
     * @returns {string} Field value
     */
    const getValue = (name) => String(formData.get(`contact[${name}]`) || '');

    const phoneCode = getValue('phone_code');
    const phone = getValue('phone');
    const fullPhone = phoneCode && phone ? `${phoneCode} ${phone}` : phone || 'Not provided';
    const companyType = getValue('company_type');
    const additionalInfo = additionalInfoTextarea?.value.trim() || '';

    let emailBody = `\n\n\n${shopName.toUpperCase()}\n`;
    emailBody += `TRADE ACCOUNT REGISTRATION REQUEST\n`;
    emailBody += `${formattedDate}\n\n\n`;

    emailBody += 'PERSONAL INFORMATION\n';
    emailBody += '───────────────────────────────────────────────────────────────────────────────\n';
    emailBody += this.createEmailRow('Title', getValue('title'));
    emailBody += this.createEmailRow('First Name', getValue('first_name'));
    emailBody += this.createEmailRow('Last Name', getValue('last_name'));
    emailBody += this.createEmailRow('Telephone', fullPhone);
    emailBody += this.createEmailRow('Email Address', getValue('email') || 'Not provided');
    emailBody += '\n';

    emailBody += 'TRADE ACCOUNT INFORMATION\n';
    emailBody += '───────────────────────────────────────────────────────────────────────────────\n';

    if (companyType) {
      emailBody += this.createEmailRow('Company Type', companyType);
      emailBody += '\n';
      emailBody += this.getCompanyTypeFields(companyType, formData);
    } else {
      emailBody += this.createEmailRow('Company Type', 'Not specified');
    }

    if (additionalInfo) {
      emailBody += '\nADDITIONAL INFORMATION\n';
      emailBody += '───────────────────────────────────────────────────────────────────────────────\n';
      emailBody += `${additionalInfo}\n`;
    }

    bodyTextarea.value = emailBody;
    if (additionalInfoTextarea) additionalInfoTextarea.value = '';
  }

  /**
   * Get company-type specific fields for email
   * @param {string} companyType - Company type
   * @param {FormData} formData - Form data
   * @returns {string} Formatted fields
   */
  getCompanyTypeFields(companyType, formData) {
    /**
     * Get form value by name
     * @param {string} name - Field name
     * @returns {string} Field value
     */
    const getValue = (name) => String(formData.get(`contact[${name}]`) || '');

    let fields = '';

    /** @type {Record<string, Array<[string, string]>>} */
    const fieldConfigs = {
      'Ltd Company': [
        ['Registered Company Name', 'registered_company_name'],
        ['Trading Name', 'trading_name'],
        ['Registered Office Address', 'registered_address'],
        ['Company Registration No', 'company_reg_number']
      ],
      'Sole Trader': [
        ['Trading Name', 'trading_name'],
        ['Trading Address', 'trading_address'],
        ['Full Name of Proprietor', 'proprietor_name']
      ],
      'Partnership': [
        ['Trading Name', 'trading_name'],
        ['Trading Address', 'trading_address'],
        ['Partner Names', 'partner_names']
      ]
    };

    const config = fieldConfigs[companyType] || [];
    config.forEach(([label, name]) => {
      const value = getValue(name);
      if (value) fields += this.createEmailRow(label, value);
    });

    /** @type {Array<[string, string]>} */
    const commonFields = [
      ['UTR Number', 'utr_number'],
      ['VAT Number', 'vat_number'],
      ['Website', 'website']
    ];

    commonFields.forEach(([label, name]) => {
      const value = getValue(name);
      if (value) fields += this.createEmailRow(label, value);
    });

    return fields;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  /** @type {NodeListOf<HTMLFormElement>} */
  const forms = document.querySelectorAll('.trade-account-form__form');
  forms.forEach((form) => {
    new TradeAccountForm(form);
  });
});
